import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ListSkeleton } from "../components/Skeleton";

type PartyType = "Supplier" | "Customer" | "Regulator" | "Partner" | "Other";

interface Party {
  uuid: string;
  partyName: string;
  partyTypes: PartyType[];
  contactName: string | null;
  contactEmail: string | null;
  isComplianceObligation: boolean;
  isActive: boolean;
  updatedAt: string;
}

const TYPE_FILTERS: Array<{ label: string; value: "" | PartyType }> = [
  { label: "All", value: "" },
  { label: "Suppliers", value: "Supplier" },
  { label: "Customers", value: "Customer" },
  { label: "Regulators", value: "Regulator" },
  { label: "Partners", value: "Partner" },
  { label: "Other", value: "Other" },
];

const TYPE_TONE: Record<PartyType, string> = {
  Supplier: "bg-blue-100 text-blue-800 dark:bg-blue-500/15 dark:text-blue-200",
  Customer: "bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200",
  Regulator: "bg-purple-100 text-purple-800 dark:bg-purple-500/15 dark:text-purple-200",
  Partner: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
  Other: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
};

const formatDateUk = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

export function Parties() {
  const { slug } = useParams<{ slug: string }>();
  const { activeOrganisation } = useAuth();
  const canWrite =
    activeOrganisation?.role === "Admin" || activeOrganisation?.role === "Contributor";
  const [filter, setFilter] = useState<"" | PartyType>("");
  const [parties, setParties] = useState<Party[] | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    setParties(null);
    const path = filter ? `/interested-parties?type=${filter}` : "/interested-parties";
    api<{ parties: Party[] }>(path)
      .then((res) => {
        if (!cancelled) setParties(res.parties);
      })
      .catch((err) => {
        if (!cancelled) setErrors(formatApiError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [filter]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { "": parties?.length ?? 0 };
    for (const t of ["Supplier", "Customer", "Regulator", "Partner", "Other"] as const) {
      c[t] = (parties ?? []).filter((p) => p.partyTypes.includes(t)).length;
    }
    return c;
  }, [parties]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
            Interested parties
          </h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Suppliers, customers, regulators and other parties whose needs and expectations affect
            your management system (ISO 9001 / 14001 / 27001 / 45001 §4.2). Includes parties that do
            not use ISO Clarity.
          </p>
        </div>
        {canWrite ? (
          <Link
            to={`/o/${slug}/parties/new`}
            className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
          >
            Add party
          </Link>
        ) : null}
      </div>

      <div role="tablist" aria-label="Filter by type" className="flex flex-wrap gap-1">
        {TYPE_FILTERS.map((f) => (
          <button
            key={f.value || "all"}
            type="button"
            role="tab"
            aria-selected={filter === f.value}
            onClick={() => setFilter(f.value)}
            className={[
              "rounded-md px-3 py-1 text-xs font-medium transition",
              filter === f.value
                ? "bg-slate-900 text-white dark:bg-slate-200 dark:text-slate-900"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700",
            ].join(" ")}
          >
            {f.label}
            {filter === "" && f.value !== "" ? (
              <span className="ml-1 text-[10px] opacity-70">({counts[f.value]})</span>
            ) : null}
          </button>
        ))}
      </div>

      {errors.length > 0 ? (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          {errors[0]}
        </div>
      ) : null}

      {parties === null ? (
        <ListSkeleton />
      ) : parties.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400">
          {filter
            ? `No parties of type '${filter}' yet.`
            : "No parties recorded yet. Click Add party to capture your first supplier, customer or regulator."}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900 dark:text-slate-400">
              <tr>
                <th className="px-4 py-2">Party</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Contact</th>
                <th className="px-4 py-2">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white dark:divide-slate-700 dark:bg-slate-800">
              {parties.map((p) => (
                <tr key={p.uuid} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                  <td className="px-4 py-2 align-top">
                    <Link
                      to={`/o/${slug}/parties/${p.uuid}`}
                      className="font-medium text-slate-900 hover:text-brand-700 dark:text-slate-100 dark:hover:text-brand-300"
                    >
                      {p.partyName}
                    </Link>
                    {p.isComplianceObligation ? (
                      <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:bg-amber-500/15 dark:text-amber-200">
                        Compliance
                      </span>
                    ) : null}
                    {!p.isActive ? (
                      <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                        Inactive
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 align-top">
                    <div className="flex flex-wrap gap-1">
                      {p.partyTypes.map((t) => (
                        <span
                          key={t}
                          className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${TYPE_TONE[t]}`}
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 align-top text-slate-600 dark:text-slate-300">
                    {p.contactName ?? "—"}
                    {p.contactEmail ? (
                      <span className="block text-xs text-slate-500 dark:text-slate-400">
                        {p.contactEmail}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 align-top text-slate-600 dark:text-slate-300">
                    {formatDateUk(p.updatedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
